import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SubstationService } from '../../../../services/substation.service';
import { Location } from '@angular/common';
import { EditSubstation } from '../../../../models/substation.model';
import { AssetStatusEnum } from '../../../../models/asset-status.model';
import { ImageType } from '../../../../models/image.model';
import { AssignTaskModalComponent } from '../../../common/assign-task-modal/assign-task-modal.component';
import {
  AttributeValue,
  SectionDefinitionEntityType,
  SectionInstance,
} from '../../../../models/section-definition.model';
import { slideToggle } from '../../../../helpers/slidetoggle';
import { EMCType } from '../../../../enums/emc-type';
import { EntityModelCustomizationService } from '../../../../services/entity-model-customization.service';
import { EntityModelConfiguration } from '../../../../enums/entityModelConfiguration';

@Component({
  selector: 'lib-edit-injection-substation',
  templateUrl: './edit-injection-substation.component.html',
  styleUrls: ['./edit-injection-substation.component.scss'],
  standalone: false,
  animations: [slideToggle],
})
export class EditInjectionSubstationComponent implements OnInit {
  @ViewChild('closeModal') closeModal?: ElementRef;
  @ViewChild('closeAudit') auditReviewModal?: ElementRef;
  @ViewChild('openUpdateDescriptionModal')
  openUpdateDescriptionModal?: ElementRef;
  @ViewChild(AssignTaskModalComponent)
  assignTaskModalComponent!: AssignTaskModalComponent;
  selectedImage: string = '';
  selectedSubstation: EditSubstation = new EditSubstation();
  id: number = 0;
  headerExpanded: boolean = false;
  editEnabled: boolean = false;
  showRecallMenu: boolean = false;
  recallAssignedAllowed: boolean = false;
  recallValidatedAllowed: boolean = false;
  informationCardExpanded: boolean = false;
  networkCardExpanded: boolean = true;
  devicesCardExpanded: boolean = false;
  busbarCardExpanded: boolean = false;
  userActivityExpanded: boolean = true;
  connectedFeedersMvPolesCardExpanded: boolean = true;
  commentsCardExpanded: boolean = false;
  imagesExpanded: boolean = true;
  isMapVisible: boolean = false;
  latitude: number = 0;
  longitude: number = 0;
  AssetAssignType = AssetStatusEnum;
  assetAssignType?: AssetStatusEnum;
  ImageType = ImageType;
  tagMountedExpanded: boolean = true;
  FullSubstationLocationImageExpanded: boolean = true;
  PowerTransformerImageExpanded: boolean = true;
  HighVoltageCircuitBreakersImageExpanded: boolean = true;
  DisconnectSwitchesImageExpanded: boolean = true;
  BusbarsImageExpanded: boolean = true;
  TsLineConnectionsImageExpanded: boolean = true;
  fieldAgentSelected: boolean = false;
  isDropdownOpened: boolean = false;
  isDataQuality: boolean = false;
  sectionInstances: SectionInstance[] = [];
  centerSections: SectionInstance[] = [];
  rightSections: SectionInstance[] = [];
  EMCConfiguration: EntityModelConfiguration[] = [];
  retagAllowed: boolean = false;
  recallAssignedTask: boolean = false;
  recallValidatedTask: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private injectionSubstationService: SubstationService,
    private toastr: ToastrService,
    private location: Location,
    private emcService: EntityModelCustomizationService,
   ) {}

  ngOnInit(): void {
    this.selectedSubstation.guid = this.route.snapshot.paramMap.get('guid') || '';
    this.injectionSubstationService.getByGuid(this.selectedSubstation.guid).subscribe({
      next: (response) => {
        this.selectedSubstation = new EditSubstation(response.body.data);
        this.latitude = this.selectedSubstation.latitude;
        this.longitude = this.selectedSubstation.longitude;
        this.id = this.selectedSubstation.id;
        if (this.selectedSubstation.status === 'Assigned') {
          this.recallAssignedAllowed = true;
        }
        if (this.selectedSubstation.dataQualityId !== 0) {
          this.recallValidatedAllowed = true;
        }
      },
      error: (error) => {
        console.error('Error fetching substation data:', error);
      },
    });
    this.injectionSubstationService
      .getInstancesForAsset(
        SectionDefinitionEntityType.InjectionSubstation,
        this.selectedSubstation.guid
      )
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.sectionInstances = (responseData.data ?? []).map(
              (section: any) => ({
                ...section,
                expanded: true
              })
            );
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (error) => {
          this.toastr.error('Error fetching section instances');
          console.error(error);
        },
      });

    this.getEMCConfiguration();
  }

  setEMCPermissions(){
    this.retagAllowed = this.EMCConfiguration.includes(EntityModelConfiguration.Retag);
    this.recallAssignedTask = this.EMCConfiguration.includes(EntityModelConfiguration.RecallAssignedTask);
    this.recallValidatedTask = this.EMCConfiguration.includes(EntityModelConfiguration.RecallValidatedTask);
  }

  private getEMCConfiguration() {
      var obj = {
        type: EMCType.InjectionSubstation,
        utilityId: null
      }
      this.emcService.getConfigurationByType(obj).subscribe({
        next: (response: any) => {
          var data = response?.body?.data;
          this.EMCConfiguration = data.map((s: any) => EntityModelConfiguration[s as keyof typeof EntityModelConfiguration]);
          this.setEMCPermissions();
        }
      })
    }

  backClicked() {
    this.location.back();
  }

  recall() {
    this.injectionSubstationService.recall(this.id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success('Successfully recalled Injection Substation.');
          window.location.reload();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  openDropdown() {
    this.isDropdownOpened = true;
  }

  selectFieldAgent(id: number) {
    this.selectedSubstation.assignedUserId = id;
    this.fieldAgentSelected = true;
  }

  assignTask = (): void => {
    if (
      this.selectedSubstation.assignedUserId != 0 &&
      this.fieldAgentSelected
    ) {
      this.injectionSubstationService
        .assignSubstation(
          this.selectedSubstation.id,
          this.selectedSubstation.assignedUserId,
          this.assetAssignType!,
          this.isDataQuality
        )
        .subscribe({
          next: (response) => {
            const responseData = response.body;
            if (responseData?.status === 200 || responseData?.status === 'OK') {
              this.toastr.success('Successfully assigned user');
              window.location.reload();
            } else {
              this.toastr.error(responseData?.message);
            }
          },
          error: (_) => {
            this.toastr.error('Error occurred');
          },
        });
    } else {
      this.toastr.warning('Please select valid user.');
    }
  };

  onAssignModalShown() {
    this.assignTaskModalComponent.title =
      this.assetAssignType == AssetStatusEnum.Retagged
        ? 'COMMON.RETAG_TASK'
        : 'COMMON.ASSIGN_AGENT';
    this.assignTaskModalComponent.submitText =
      this.assetAssignType == AssetStatusEnum.Retagged
        ? 'COMMON.RETAG'
        : 'COMMON.ASSIGN_TASK';
    this.assignTaskModalComponent.getMobileUsers();
  }

  onModalAssignFade() {
    this.assignTaskModalComponent.filterText = '';
    this.assignTaskModalComponent.isDropdownOpened = false;
  }

  cancelAuditReview() {
    this.auditReviewModal?.nativeElement?.click();
  }

  getImage(imageType?: ImageType): string | undefined {
    return this.selectedSubstation.images?.find(
      (x) =>
        ImageType[x.imageType.toString() as keyof typeof ImageType] == imageType
    )?.url;
  }

  showImage(type: ImageType) {
    this.selectedImage = this.getImage(type) || '';
  }

  fileBrowserHandler(e: any) {}

  setClickedLocation(attributeValue: AttributeValue | undefined) {
    if (attributeValue === undefined) {
      this.latitude = this.selectedSubstation.latitude;
      this.longitude = this.selectedSubstation.longitude;
    } else {
      this.latitude = attributeValue.latitude!;
      this.longitude = attributeValue.longitude!;
    }

    this.isMapVisible = !this.isMapVisible;
  }
}
